import { failure, isFailure, isOk, isPending, ok, pending, Result } from "@/common/common";
import { parseDateInput, toLocalDateISOString, today } from "@/utils/Dates";
import { DCEvent, EventDispatcher, UpdateStoreEvent, WithStoreEvent } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { customFieldsApiClient } from "./customFieldsApiClient";
import {
  CustomFieldDataType,
  CustomFieldRequirement,
  systemFieldLabels,
} from "./customFieldsDomain";
import {
  blankUnmappedCells,
  cellEditor,
  errorCount,
  gridColumns,
  gridRowSource,
  ImportFieldOption,
  indexIssues,
  resultRows,
  SpreadsheetImportGrid,
  SpreadsheetImportGridProps,
  SpreadsheetImportIssue,
  SpreadsheetImportResult,
  SpreadsheetImportRow,
  spreadsheetRejectionMessage,
  takenElsewhere,
  withCell,
  withColumnField,
  withRow,
} from "./spreadsheetImportDomain";

export type CompanyDataField = "COMPANY_NAME" | "TIN_NUMBER" | "DATE_OF_INCORPORATION";

export type AddressPart =
  | "ADDRESS_LINE1"
  | "ADDRESS_LINE2"
  | "POSTAL_CODE"
  | "POSTAL_PLACE"
  | "COUNTRY";

export type EntityDataTarget =
  | { targetType: "SYSTEM"; field: CompanyDataField }
  | { targetType: "ADDRESS"; addressType: "BUSINESS" | "POSTAL"; part: AddressPart }
  | { targetType: "CUSTOM"; definitionId: string };

export type EntityDataImportField = {
  target: EntityDataTarget;
  dataType: CustomFieldDataType;
  name?: string;
  options?: string[];
  effectiveDate?: CustomFieldRequirement;
};

export type EntityDataImportSheet = {
  headers: string[];
  rows: SpreadsheetImportRow[];
  fields: EntityDataImportField[];
  countryCodes: string[];
  fieldCountryCodes: string[];
};

export type EntityDataImportRequest = {
  rows: SpreadsheetImportRow[];
  columns: (EntityDataTarget | null)[];
  effectiveFrom?: string;
};

export type EntityDataImportResponse = {
  updated: number;
  unchanged: number;
  issues: SpreadsheetImportIssue[];
};

type EntityDataImportSetup = Omit<EntityDataImportSheet, "rows">;

export type EntityDataImportState = {
  accountId: string;
  setupResult: Result<EntityDataImportSetup, TMessage>;
  rows: SpreadsheetImportRow[];
  columns: (string | undefined)[];
  effectiveDate: string;
  errorsOnly: boolean;
  selectedOnly: boolean;
  revision: number;
  validation?: Result<EntityDataImportResponse>;
  resultRevision?: number;
  importResult?: Result<EntityDataImportResponse>;
  afterImport: DCEvent[];
};

const addressLabels: Record<"BUSINESS" | "POSTAL", TMessage> = {
  BUSINESS: systemFieldLabels.BUSINESS_ADDRESS,
  POSTAL: systemFieldLabels.POSTAL_ADDRESS,
};

const addressPartLabels: Record<AddressPart, TMessage> = {
  ADDRESS_LINE1: { key: "entity-data.address-part-line1" },
  ADDRESS_LINE2: { key: "entity-data.address-part-line2" },
  POSTAL_CODE: { key: "entity-data.address-part-postal-code" },
  POSTAL_PLACE: { key: "entity-data.address-part-postal-place" },
  COUNTRY: { key: "entity-data.address-part-country" },
};

export const fieldKey = (target: EntityDataTarget): string => {
  switch (target.targetType) {
    case "SYSTEM":
      return `SYSTEM:${target.field}`;
    case "ADDRESS":
      return `ADDRESS:${target.addressType}:${target.part}`;
    case "CUSTOM":
      return `CUSTOM:${target.definitionId}`;
  }
};

export const fieldLabel = (field: EntityDataImportField): TMessage => {
  const target = field.target;
  switch (target.targetType) {
    case "SYSTEM":
      return systemFieldLabels[target.field];
    case "ADDRESS":
      return {
        key: "entity-data.spreadsheet-address-field",
        params: {
          address: addressLabels[target.addressType],
          part: addressPartLabels[target.part],
        },
      };
    case "CUSTOM":
      return { translated: field.name ?? "" };
  }
};

const fieldSection = (field: EntityDataImportField): TMessage =>
  field.target.targetType === "CUSTOM"
    ? { key: "entity-data.spreadsheet-custom-fields" }
    : { key: "entity-data.spreadsheet-system-fields" };

const updateEntityDataImport = (
  update: (state: EntityDataImportState) => EntityDataImportState,
): UpdateStoreEvent => ({
  type: "update-store",
  data: { entityDataImport: (state) => state && update(state) },
});

const closeEntityDataImport = (): UpdateStoreEvent => ({
  type: "update-store",
  data: { entityDataImport: () => undefined },
});

const maxEntityDataRows = 10000;

export const openEntityDataImport = (
  accountId: string,
  files: File[] | null,
  afterImport: DCEvent[],
): DCEvent[] => {
  const file = files?.[0];
  if (!file) {
    return [];
  }
  return [
    {
      type: "update-store",
      data: {
        entityDataImport: () => ({
          accountId,
          setupResult: pending(),
          rows: [],
          columns: [],
          effectiveDate: "",
          errorsOnly: false,
          selectedOnly: false,
          revision: 0,
          afterImport,
        }),
      },
    },
    customFieldsApiClient.readEntityDataImport(accountId, file, {
      onSuccess: (res, rootState) => {
        const { rows, ...setup } = res.data;
        const next = rootState.entityDataImport && {
          ...rootState.entityDataImport,
          setupResult: ok(setup),
          rows,
          columns: setup.headers.map(() => undefined),
          revision: rootState.entityDataImport.revision + 1,
        };
        return next ? [updateEntityDataImport(() => next), ...validate(next)] : [];
      },
      onFailure: (err) => [
        updateEntityDataImport((state) => ({
          ...state,
          setupResult: failure(spreadsheetRejectionMessage(err, maxEntityDataRows)),
        })),
      ],
    }),
  ];
};

const noSetup: EntityDataImportSetup = {
  headers: [],
  fields: [],
  countryCodes: [],
  fieldCountryCodes: [],
};

const setupOf = (state: EntityDataImportState): EntityDataImportSetup =>
  isOk(state.setupResult) ? state.setupResult.data : noSetup;

const fieldsByKeyOf = (state: EntityDataImportState): Map<string, EntityDataImportField> =>
  new Map(setupOf(state).fields.map((field) => [fieldKey(field.target), field]));

const effectiveDateIssue = (state: EntityDataImportState): TMessage | undefined => {
  const { parsed, error } = parseDateInput(state.effectiveDate);
  if (error) return { key: "date-picker.invalid-date-message" };
  if (parsed && parsed > today()) return { key: "entity-data.spreadsheet-date-after-today" };
  return undefined;
};

const effectiveFromOf = (state: EntityDataImportState): string | undefined =>
  state.effectiveDate && !effectiveDateIssue(state) ? state.effectiveDate : undefined;

export const importRequest = (state: EntityDataImportState): EntityDataImportRequest => {
  const fieldsByKey = fieldsByKeyOf(state);
  const columns = state.columns.map((key) => (key && fieldsByKey.get(key)?.target) || null);
  return {
    rows: blankUnmappedCells(state.rows, (index) => columns[index] !== null),
    columns,
    effectiveFrom: effectiveFromOf(state),
  };
};

const validate = (state: EntityDataImportState): DCEvent[] => {
  const revision = state.revision;
  const applies = (current: EntityDataImportState) => current.revision === revision;
  return [
    customFieldsApiClient.validateEntityDataImport(state.accountId, importRequest(state), {
      onSuccess: (res) => [
        updateEntityDataImport((current) =>
          applies(current)
            ? { ...current, validation: ok(res.data), resultRevision: revision }
            : current,
        ),
      ],
      onFailure: () => [
        updateEntityDataImport((current) =>
          applies(current)
            ? { ...current, validation: failure(), resultRevision: revision }
            : current,
        ),
      ],
    }),
  ];
};

const withImportState = (f: (state: EntityDataImportState) => DCEvent[]): WithStoreEvent => ({
  type: "with-store",
  events: (root) => (root.entityDataImport ? f(root.entityDataImport) : []),
});

const changeAndValidate = (
  change: (state: EntityDataImportState) => EntityDataImportState,
): WithStoreEvent =>
  withImportState((state) => {
    const changed = change(state);
    if (changed === state) {
      return [];
    }
    const next = { ...changed, revision: state.revision + 1 };
    return [updateEntityDataImport(() => next), ...validate(next)];
  });

const selectColumnField = (column: number, key: string | undefined): WithStoreEvent =>
  changeAndValidate((state) => withColumnField(state, column, key));

const changeEffectiveDate = (effectiveDate: string): WithStoreEvent =>
  changeAndValidate((state) =>
    state.effectiveDate === effectiveDate ? state : { ...state, effectiveDate },
  );

const changeCountryCode = (rowNumber: number, countryCode: string): WithStoreEvent =>
  changeAndValidate((state) => withRow(state, rowNumber, { countryCode }));

const changeOrgNo = (rowNumber: number, orgNo: string): WithStoreEvent =>
  changeAndValidate((state) => withRow(state, rowNumber, { orgNo }));

const changeCell = (rowNumber: number, column: number, text: string): WithStoreEvent =>
  changeAndValidate((state) => withCell(state, rowNumber, column, text));

const submitImport = (state: EntityDataImportState): DCEvent[] => [
  updateEntityDataImport((current) => ({ ...current, importResult: pending() })),
  customFieldsApiClient.importEntityData(state.accountId, importRequest(state), {
    onSuccess: (res) => [
      updateEntityDataImport((current) => ({ ...current, importResult: ok(res.data) })),
      ...state.afterImport,
    ],
    onFailure: () => [
      updateEntityDataImport((current) => ({ ...current, importResult: failure() })),
    ],
  }),
];

const effectiveDateTag = (
  field: EntityDataImportField,
  effectiveFrom: string | undefined,
): TMessage | undefined => {
  if (!field.effectiveDate) return undefined;
  if (effectiveFrom) {
    return {
      key: "entity-data.spreadsheet-effective-from",
      params: { date: { type: "date", value: effectiveFrom } },
    };
  }
  return field.effectiveDate === "REQUIRED"
    ? undefined
    : { key: "entity-data.spreadsheet-effective-from-today" };
};

const fieldOption = (
  field: EntityDataImportField,
  effectiveFrom: string | undefined,
): ImportFieldOption => ({
  key: fieldKey(field.target),
  label: fieldLabel(field),
  section: fieldSection(field),
  editor: cellEditor(field.dataType, field.options),
  tag: effectiveDateTag(field, effectiveFrom),
});

export const gridOf = (state: EntityDataImportState): SpreadsheetImportGrid => ({
  headers: setupOf(state).headers,
  rows: state.rows,
  columns: state.columns,
  fieldsByKey: new Map(
    setupOf(state).fields.map((field) => [
      fieldKey(field.target),
      fieldOption(field, effectiveFromOf(state)),
    ]),
  ),
  errorsOnly: state.errorsOnly,
  selectedOnly: state.selectedOnly,
});

export type EntityDataImportViewProps = SpreadsheetImportGridProps & {
  setupResult: Result<EntityDataImportSetup, TMessage>;
  effectiveDate: string;
  effectiveDateIssue?: TMessage;
  maxEffectiveDate: string;
  canImport: boolean;
  isImporting: boolean;
  importFailed: boolean;
  result?: SpreadsheetImportResult;
  onChangeEffectiveDate: (input: string) => void;
  onImport: () => void;
  onClose: () => void;
};

const issuesOf = (
  result: Result<EntityDataImportResponse> | undefined,
): SpreadsheetImportIssue[] => (isOk(result) ? result.data.issues : []);

export const prepEntityDataImportView = (
  state: EntityDataImportState | undefined,
  dispatch: EventDispatcher,
): EntityDataImportViewProps | undefined => {
  if (!state) {
    return undefined;
  }
  const issues = issuesOf(state.validation);
  const index = indexIssues(issues);
  const grid = gridOf(state);
  const columns = gridColumns(grid, index);
  const rows = gridRowSource(grid, index, columns);
  const importResult = state.importResult;
  return {
    setupResult: state.setupResult,
    fieldsByKey: grid.fieldsByKey,
    countryCodes: setupOf(state).countryCodes,
    fieldCountryCodes: setupOf(state).fieldCountryCodes,
    gridColumns: columns,
    gridRows: rows,
    rowCount: state.rows.length,
    errorCount: errorCount(issues),
    effectiveDate: state.effectiveDate,
    effectiveDateIssue: effectiveDateIssue(state),
    maxEffectiveDate: toLocalDateISOString(today()),
    errorsOnly: state.errorsOnly,
    selectedOnly: state.selectedOnly,
    isValidating: state.resultRevision !== state.revision,
    canImport:
      state.columns.some((key) => key !== undefined) &&
      !effectiveDateIssue(state) &&
      isOk(state.setupResult),
    isImporting: isPending(importResult),
    importFailed: isFailure(importResult),
    result: isOk(importResult)
      ? {
          counts: [
            {
              key: "updated",
              label: { key: "entity-data.import-updated" },
              value: importResult.data.updated,
            },
            {
              key: "unchanged",
              label: { key: "entity-data.import-unchanged" },
              value: importResult.data.unchanged,
            },
            {
              key: "rejected",
              label: { key: "entity-data.spreadsheet-not-imported" },
              value: importResult.data.issues.length,
            },
          ],
          nothingImported: importResult.data.updated === 0,
          rows: resultRows(grid, importResult.data.issues),
        }
      : undefined,
    isTakenElsewhere: takenElsewhere(state.columns),
    onSelectField: (column, key) => dispatch([selectColumnField(column, key)]),
    onChangeCountryCode: (row, countryCode) => dispatch([changeCountryCode(row, countryCode)]),
    onChangeOrgNo: (row, orgNo) => dispatch([changeOrgNo(row, orgNo)]),
    onChangeCell: (row, column, text) => dispatch([changeCell(row, column, text)]),
    onChangeEffectiveDate: (input) => dispatch([changeEffectiveDate(input)]),
    onToggleErrorsOnly: () =>
      dispatch([
        updateEntityDataImport((current) => ({ ...current, errorsOnly: !current.errorsOnly })),
      ]),
    onToggleSelectedOnly: () =>
      dispatch([
        updateEntityDataImport((current) => ({ ...current, selectedOnly: !current.selectedOnly })),
      ]),
    onImport: () => dispatch(submitImport(state)),
    onClose: () => dispatch([closeEntityDataImport()]),
  };
};
